import React from "react";
import { Tag, Typography } from "antd";
import { RiseOutlined, FallOutlined } from "@ant-design/icons";

const { Text } = Typography;

export const PARAM_META = {
  "Critical defect score weight": {
    title: "Critical defect score",
    description: "Weight of critical defect score",
  },
  "Specification score weight": {
    title: "Specification score",
    description: "Weight of specification score",
  },
  "Regulatory score weight": {
    title: "Regulatory compliance score",
    description: "Weight of regulatory compliance score",
  },
  "Raw findings rate normalised weight": {
    title: "Raw findings rate",
    description: "Weight of raw findings rate (normalised)",
  },
  "Task score weight": {
    title: "Task score",
    description: "Weight of task score",
  },
  "Cost normalised weight": {
    title: "Cost",
    description: "Weight of cost (normalised)",
  },
};

const KEY_META_FALLBACK = {
  critical_defect_score_weight: {
    title: "Critical defect score",
    description: "Weight of critical defect score",
  },
  specification_score_weight: {
    title: "Specification score",
    description: "Weight of specification score",
  },
  regulatory_score_weight: {
    title: "Regulatory compliance score",
    description: "Weight of regulatory compliance score",
  },
  raw_findings_rate_normalised_weight: {
    title: "Raw findings rate",
    description: "Weight of raw findings rate (normalised)",
  },
  task_score_weight: {
    title: "Task score",
    description: "Weight of task score",
  },
  cost_normalised_weight: {
    title: "Cost",
    description: "Weight of cost (normalised)",
  },
  // New /ranking-api/playground/ranking_weights parameter slugs
  critical_score: {
    title: "Critical defect score",
    description: "Weight of critical defect score",
  },
  spec_score: {
    title: "Specification score",
    description: "Weight of specification score",
  },
  reg_score: {
    title: "Regulatory compliance score",
    description: "Weight of regulatory compliance score",
  },
  raw_norm: {
    title: "Raw findings rate",
    description: "Weight of raw findings rate (normalised)",
  },
  task_score: {
    title: "Task score",
    description: "Weight of task score",
  },
  cost_norm: {
    title: "Cost",
    description: "Weight of cost (normalised)",
  },
};

export function getParamMeta(label, key) {
  if (label && PARAM_META[label]) {
    return PARAM_META[label];
  }
  if (key && KEY_META_FALLBACK[key]) {
    return KEY_META_FALLBACK[key];
  }
  return {
    title: label || key || "Parameter",
    description: `Weight of ${String(label || key || "").toLowerCase()}`,
  };
}

export const ModifiedTag = () => (
  <Tag color="blue" className="playground_modified_tag">
    Modified
  </Tag>
);

// Rank: a lower rank number is better. direction comes straight from the API.
const RankChangeCell = ({ delta, direction }) => {
  if (!delta || direction === "same") {
    return (
      <Text type="secondary">
        <span style={{ fontSize: 11 }}>–</span>
      </Text>
    );
  }
  const isBetter = direction === "up";
  const color = isBetter ? "#3f8600" : "#cf1322";
  const Icon = isBetter ? RiseOutlined : FallOutlined;
  return (
    <Text style={{ color, fontWeight: 500 }}>
      <Icon style={{ fontSize: 11, marginRight: 4 }} />
      {Math.abs(delta)}
    </Text>
  );
};

// WPM score: a higher score is better, shown as a signed value (no arrow).
const ScoreChangeCell = ({ delta, decimals = 3 }) => {
  if (!delta) {
    return <Text type="secondary">–</Text>;
  }
  const isBetter = delta > 0;
  const color = isBetter ? "#3f8600" : "#cf1322";
  const sign = isBetter ? "+" : "-";
  return (
    <Text style={{ color, fontWeight: 500 }}>
      {sign}
      {Math.abs(delta).toFixed(decimals)}
    </Text>
  );
};

const DiffCell = ({ diff }) => {
  if (!diff) {
    return <Text type="secondary">–</Text>;
  }
  const isSavings = diff < 0;
  const color = isSavings ? "#3f8600" : "#cf1322";
  const sign = isSavings ? "-" : "+";
  return (
    <Text style={{ color, fontWeight: 500 }}>
      {sign}
      {Math.abs(Math.round(diff)).toLocaleString("en-US")}
    </Text>
  );
};

export const getAlgorithmComparisonColumns = () => [
  {
    title: "Spec No.",
    dataIndex: "spec_concat",
    key: "spec_concat",
    fixed: "left",
    width: 130,
    render: (text) => <Text strong>{text}</Text>,
  },
  {
    title: "Card No.",
    dataIndex: "card_no",
    key: "card_no",
    fixed: "left",
    align: "center",
    width: 130,
  },
  {
    title: "Rank",
    children: [
      {
        title: "Previous",
        dataIndex: "rankPrevious",
        key: "rankPrevious",
        align: "center",
        width: 90,
        render: (v) => (v != null ? v : "–"),
      },
      {
        title: "Proposed",
        dataIndex: "rankWhatIf",
        key: "rankWhatIf",
        align: "center",
        width: 90,
        render: (v) => (v != null ? v : "–"),
      },
      {
        title: "Change",
        key: "rankChange",
        align: "center",
        width: 90,
        render: (_, row) => (
          <RankChangeCell delta={row.rankDelta} direction={row.direction} />
        ),
      },
    ],
  },
  {
    title: "WPM Score",
    children: [
      {
        title: "Previous",
        dataIndex: "wpmPrevious",
        key: "wpmPrevious",
        align: "center",
        width: 90,
        render: (v) => (v != null ? Number(v).toFixed(3) : "–"),
      },
      {
        title: "Proposed",
        dataIndex: "wpmWhatIf",
        key: "wpmWhatIf",
        align: "center",
        width: 90,
        render: (v) => (v != null ? Number(v).toFixed(3) : "–"),
      },
      {
        title: "Change",
        key: "wpmChange",
        align: "center",
        width: 90,
        render: (_, row) => <ScoreChangeCell delta={row.wpmDelta} />,
      },
    ],
  },
  {
    title: "Total Labor Cost Max ($)",
    children: [
      {
        title: "Previous",
        dataIndex: "costPrevious",
        key: "costPrevious",
        align: "right",
        width: 130,
        render: (v) => (v != null ? Number(v).toLocaleString("en-US") : "–"),
      },
      {
        title: "Proposed",
        dataIndex: "costWhatIf",
        key: "costWhatIf",
        align: "right",
        width: 130,
        render: (v) => (v != null ? Number(v).toLocaleString("en-US") : "–"),
      },
      {
        title: "Difference",
        key: "costChange",
        align: "right",
        width: 110,
        render: (_, row) => <DiffCell diff={row.costDelta} />,
      },
    ],
  },
];
